import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { useTranslation } from 'react-i18next';
import {
  SELF_SERVE_PLANS,
  selfServePlan,
  PLAN_FEATURE_ROWS,
  planCreditsPerMonth,
  planFeatureLabel,
  resolveEntitlement,
  type SelfServePlanDisplay,
  type PlanFeatureRowDef,
} from '@traaker/shared';
import { api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatNumber } from '../../i18n/format';
import { EngineLogo, ALL_ENGINES } from '../brand/EngineLogo';

type Interval = 'month' | 'year';
/** select = fresh pick (not yet subscribed); the others are relative to current tier. */
type PlanCardState = 'select' | 'current' | 'upgrade' | 'downgrade';

const TIER_RANK: Record<string, number> = { basic: 0, pro: 1, elite: 2 };
const fmtEur = (cents: number) => `${formatNumber(Math.round(cents / 100))} €`;
/** Yearly shown as an equivalent monthly (yearly / 12) so cards compare like-for-like. */
const yearlyPerMonth = (yearlyCents: number) => `${formatNumber(Math.round(yearlyCents / 12 / 100))} €`;

/** registry engine id ('chatgpt') → run-path id ('openai') used by EngineLogo. */
function toRunPathEngine(id: string): string {
  return id === 'chatgpt' ? 'openai' : id;
}

/**
 * Plan-selection popup for self-serve tiers. Shows all three tiers (Basic / Pro /
 * Elite) as cards — Basic lists its baseline features, Pro + Elite are framed as
 * "Basic + these additions" — with a monthly/yearly toggle (yearly = 2 months free),
 * and starts checkout on the chosen {tier, interval}.
 *
 * Selectability depends on `subscribed` (is the tenant on a paid active sub):
 *  - NOT subscribed (trialing / pending) → EVERY tier is selectable, including
 *    Basic (the user is starting their subscription and Basic is the standard plan).
 *  - subscribed → the current tier renders "Offre actuelle" (disabled), lower tiers
 *    are downgrade-via-Portal (disabled here), higher tiers are "Choisir".
 *
 * Feature rows are derived at render time from `PLAN_FEATURE_ROWS` +
 * `resolveEntitlement` (never hardcoded strings) — every card lists the SAME
 * metrics in the SAME order, translated in the current UI language, and AI
 * engines render as logos instead of a text list. `highlightMetric` (optional)
 * visually emphasizes one specific row across all 3 cards — used when the
 * dialog is opened from a specific quota's "upgrade" CTA (e.g. "Analyser plus
 * de pages" → highlights the pages-per-audit row on every card).
 *
 * `currentTier === 'elite'` skips the card grid entirely: Elite is the top
 * self-serve tier, so every card would render disabled/current and the only
 * real next step is Enterprise — showing that grid on every quota-exceeded
 * CTA was a confusing "upgrade" popup with nothing upgradeable in it.
 */
export default function PlanSelectionDialog({
  open,
  onClose,
  currentTier,
  subscribed,
  highlightMetric,
}: {
  open: boolean;
  onClose: () => void;
  currentTier: string;
  /** True when the tenant has a paid active subscription (not trialing/pending). */
  subscribed: boolean;
  /** Metric key (from PLAN_FEATURE_ROWS) to highlight on every card, e.g. 'pages_per_website_audit'. */
  highlightMetric?: string;
}) {
  const { t, i18n } = useTranslation();
  const lang: 'fr' | 'en' = i18n.language?.startsWith('en') ? 'en' : 'fr';
  const { refreshProfile } = useAuth();
  const [interval, setInterval] = useState<Interval>('month');
  const [busyTier, setBusyTier] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Upgrade pending confirmation (only for an already-subscribed tenant — the
  // swap charges immediately). A fresh subscribe skips this (Stripe Checkout is
  // itself the confirmation).
  const [confirmTier, setConfirmTier] = useState<string | null>(null);

  const currentRank = TIER_RANK[currentTier] ?? -1;

  // Promotion code (docs/billing/pricing-and-billing.md §4.9): always typed in
  // Stripe Checkout's own field, shown on every session; Stripe owns every rule
  // (product / interval scope, new customers only). An email link's `?promo=`
  // (E4/E5 lifecycle emails → `/settings?tab=billing&promo=TRIAL50`) is only
  // DISPLAYED here, so the customer knows which code to type.
  const [promotionCode] = useState(
    () => new URLSearchParams(window.location.search).get('promo')?.trim() || '',
  );

  const stateFor = (tier: string): PlanCardState => {
    // Not yet subscribed → any tier is a fresh choice (Basic included).
    if (!subscribed) return 'select';
    const rank = TIER_RANK[tier] ?? -1;
    if (rank === currentRank) return 'current';
    return rank < currentRank ? 'downgrade' : 'upgrade';
  };

  // Subscribed upgrade → confirm first (immediate prorated charge); otherwise go
  // straight to checkout/swap.
  const onPick = (tier: string) => {
    if (subscribed && stateFor(tier) === 'upgrade') setConfirmTier(tier);
    else choose(tier);
  };

  const choose = async (tier: string) => {
    setError(null);
    setBusyTier(tier);
    try {
      const res = await api.post<{
        checkoutUrl?: string;
        swapped?: boolean;
        direction?: 'upgrade' | 'downgrade';
        effectiveAt?: string;
      }>('/billing/checkout/self-serve', {
        tier,
        interval,
      });
      if (res.checkoutUrl) {
        // New subscription → full Stripe Checkout.
        window.location.href = res.checkoutUrl;
        return;
      }
      // In-place upgrade on a live sub (no redirect). Refresh the profile so the
      // new tier reflects, then close.
      setConfirmTier(null);
      await refreshProfile();
      onClose();
    } catch (e: any) {
      setConfirmTier(null);
      setError(e?.data?.message || e?.message || t('planSelection.errorStart'));
      setBusyTier(null);
    }
  };

  return (
    <Dialog open={open} onClose={() => (busyTier ? undefined : onClose())} maxWidth="md" fullWidth>
      <Box sx={{ position: 'relative', p: { xs: 2.5, sm: 4 } }}>
        <IconButton
          onClick={onClose}
          disabled={!!busyTier}
          sx={{ position: 'absolute', top: 12, right: 12, color: 'text.secondary' }}
        >
          <CloseRoundedIcon />
        </IconButton>

        <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: '-0.02em' }}>
          {t('planSelection.title')}
        </Typography>

        {currentTier === 'elite' ? (
          // Elite is the top self-serve tier — there's nothing left to sell in
          // this dialog. Skip the (all-disabled/current) card grid entirely and
          // point straight at Enterprise instead of showing a confusing popup.
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                mx: 'auto',
                mb: 2,
                borderRadius: '50%',
                bgcolor: 'primary.50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <WorkspacePremiumIcon sx={{ fontSize: 30, color: 'primary.main' }} />
            </Box>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 420, mx: 'auto' }}>
              {t('planSelection.eliteMaxedBody')}
            </Typography>
            <Button
              variant="contained"
              href="https://traaker.ai/contact.html"
              target="_blank"
              rel="noreferrer"
              sx={{ mt: 3 }}
            >
              {t('planSelection.eliteMaxedCta')}
            </Button>
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
              {subscribed ? t('planSelection.subtitleSubscribed') : t('planSelection.subtitleNew')}
            </Typography>

            {/* Monthly / yearly toggle */}
            <Stack direction="row" justifyContent="center" sx={{ mb: 3 }}>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={interval}
                onChange={(_, v) => v && setInterval(v)}
                sx={{ bgcolor: 'background.paper' }}
              >
                <ToggleButton value="month" sx={{ px: 2.5, textTransform: 'none', fontWeight: 700 }}>
                  {t('planSelection.monthly')}
                </ToggleButton>
                <ToggleButton value="year" sx={{ px: 2.5, textTransform: 'none', fontWeight: 700 }}>
                  {t('planSelection.yearly')}
                  <Chip
                    label={t('planSelection.yearlyBadge')}
                    size="small"
                    color="success"
                    sx={{ ml: 1, height: 18, fontSize: 10, fontWeight: 700 }}
                  />
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            {/* Promotion code: typed in Stripe Checkout's own field (every session). */}
            <Stack alignItems="center" sx={{ mb: 3 }}>
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                {promotionCode
                  ? t('planSelection.promoFromLink', { code: promotionCode.toUpperCase() })
                  : t('planSelection.promoAtCheckout')}
              </Typography>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
              {SELF_SERVE_PLANS.map((plan) => (
                <PlanCard
                  key={plan.tier}
                  plan={plan}
                  interval={interval}
                  state={stateFor(plan.tier)}
                  busy={busyTier === plan.tier}
                  disabled={!!busyTier}
                  onChoose={() => onPick(plan.tier)}
                  lang={lang}
                  highlightMetric={highlightMetric}
                />
              ))}
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2.5, textAlign: 'center' }}>
              {t('planSelection.footerText')}{' '}
              <a href="https://traaker.ai/contact.html" target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>
                {t('planSelection.footerLink')}
              </a>
              .
            </Typography>
          </>
        )}
      </Box>

      {/* Upgrade confirmation — only reached for an already-subscribed tenant (the
          swap charges immediately). Shows the new price + that a prorated amount
          is billed now. No precise € estimate: the self-serve renewal date isn't
          on the client, so we state the prorated-charge fact truthfully rather
          than show a number we can't compute accurately. */}
      <UpgradeConfirm
        open={!!confirmTier}
        plan={confirmTier ? selfServePlan(confirmTier) : undefined}
        interval={interval}
        busy={!!busyTier}
        onClose={() => setConfirmTier(null)}
        onConfirm={() => confirmTier && choose(confirmTier)}
      />
    </Dialog>
  );
}

function UpgradeConfirm({
  open,
  plan,
  interval,
  busy,
  onClose,
  onConfirm,
}: {
  open: boolean;
  plan?: SelfServePlanDisplay;
  interval: Interval;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  if (!plan) return null;
  const price = interval === 'month' ? fmtEur(plan.monthlyCents) : t('planSelection.pricePerYear', { price: fmtEur(plan.yearlyCents) });
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
          {t('planSelection.upgradeTitle', { plan: plan.label })}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {plan.label} — <strong>{t('planSelection.priceHt', { price })}</strong>{interval === 'month' ? t('planSelection.perMonthSuffix') : ''}.
        </Typography>
        <Box sx={{ bgcolor: '#F5F3FF', borderRadius: 2, p: 2, mb: 2 }}>
          <Stack direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
            <Typography variant="caption" color="text.secondary">{t('planSelection.effectLabel')}</Typography>
            <Typography variant="caption" fontWeight={700}>{t('planSelection.effectValue')}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
            <Typography variant="caption" color="text.secondary">{t('planSelection.billingLabel')}</Typography>
            <Typography variant="caption" fontWeight={700}>{t('planSelection.billingValue')}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
            <Typography variant="caption" color="text.secondary">{t('planSelection.fullPriceLabel')}</Typography>
            <Typography variant="caption" fontWeight={700}>{t('planSelection.fullPriceValue')}</Typography>
          </Stack>
        </Box>
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button onClick={onClose} disabled={busy}>{t('planSelection.cancel')}</Button>
          <Button variant="contained" onClick={onConfirm} disabled={busy}>
            {busy ? '…' : t('planSelection.confirmAndPay')}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
}

/** One resolved feature row, ready to render (text OR engine logos). */
interface ResolvedRow {
  row: PlanFeatureRowDef;
  /** Rendered sentence, e.g. "100 prompts (au lieu de 30)" — empty for engine rows. */
  text: string;
  /** Only for kind === 'engines': every engine id available on this tier, run-path convention. */
  engineIds?: string[];
}

/** Order a set of allowed registry engine ids by the canonical roster order. */
function orderEngines(allowed: string[]): string[] {
  const set = new Set(allowed.map(toRunPathEngine));
  return ALL_ENGINES.filter((e) => set.has(e.id)).map((e) => e.id);
}

/**
 * Resolve this plan's feature rows in the canonical `PLAN_FEATURE_ROWS` order.
 * Every card lists its OWN full set (2026-11-04, owner: the « Tout Starter,
 * plus : » delta framing with « (au lieu de N) » suffixes was confusing), so the
 * same rows line up across the three cards.
 */
function resolveRows(tier: string, lang: 'fr' | 'en'): ResolvedRow[] {
  const rows: ResolvedRow[] = [];
  for (const row of PLAN_FEATURE_ROWS) {
    if (row.kind === 'engines') {
      // Always show the FULL engine roster for this tier (not just the delta
      // over Basic) — a prospect comparing cards needs to see every model
      // included, not just what changed.
      const tierAllowed = orderEngines(resolveEntitlement('engines', tier).allowed || []);
      if (tierAllowed.length) rows.push({ row, text: '', engineIds: tierAllowed });
      continue;
    }
    if (row.kind === 'credits') {
      // Monthly credit allocation (website audit, Earned Media, content creation).
      const credits = planCreditsPerMonth(tier);
      if (credits > 0) rows.push({ row, text: planFeatureLabel(row.metricKey, credits, lang) });
      continue;
    }
    if (row.kind === 'boolean') {
      const on = resolveEntitlement(row.metricKey, tier).limit === 1;
      if (on) rows.push({ row, text: planFeatureLabel(row.metricKey, 1, lang) });
      continue;
    }
    // kind === 'metric'
    rows.push({ row, text: planFeatureLabel(row.metricKey, resolveEntitlement(row.metricKey, tier).limit, lang) });
  }
  return rows;
}

function PlanCard({
  plan,
  interval,
  state,
  busy,
  disabled,
  onChoose,
  lang,
  highlightMetric,
}: {
  plan: SelfServePlanDisplay;
  interval: Interval;
  state: PlanCardState;
  busy: boolean;
  disabled: boolean;
  onChoose: () => void;
  lang: 'fr' | 'en';
  highlightMetric?: string;
}) {
  const { t } = useTranslation();
  const isBasic = plan.tier === 'basic';
  // Recommended highlight only when the tier is actually pickable.
  const featured = !!plan.recommended && (state === 'upgrade' || state === 'select');
  const priceMain = interval === 'month' ? fmtEur(plan.monthlyCents) : yearlyPerMonth(plan.yearlyCents);
  const rows = useMemo(() => resolveRows(plan.tier, lang), [plan.tier, lang]);

  return (
    <Box
      sx={{
        position: 'relative',
        p: 3,
        borderRadius: 3,
        border: '1px solid',
        borderColor: state === 'current' ? 'success.main' : featured ? 'primary.main' : 'divider',
        bgcolor: featured ? 'primary.50' : 'background.paper',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {featured && (
        <Chip
          label={t('planSelection.recommended')}
          color="primary"
          size="small"
          sx={{ position: 'absolute', top: -11, left: 20, height: 22, fontWeight: 700 }}
        />
      )}

      <Typography variant="h6" fontWeight={800}>{plan.label}</Typography>

      <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ mt: 1 }}>
        <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: '-0.02em' }}>{priceMain}</Typography>
        <Typography variant="body2" color="text.secondary">{t('planSelection.perMonthHt')}</Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ minHeight: 18 }}>
        {interval === 'year'
          ? t('planSelection.billedYearly', { price: fmtEur(plan.yearlyCents) })
          : t('planSelection.billedMonthly')}
      </Typography>

      <Stack spacing={1} sx={{ mt: 2.5, flexGrow: 1 }}>
        {rows.map(({ row, text, engineIds }) => {
          const highlighted = highlightMetric === row.metricKey;
          return (
            <Stack
              key={row.metricKey}
              direction="row"
              spacing={1}
              alignItems="flex-start"
              sx={
                highlighted
                  ? { bgcolor: '#FEF3C7', borderRadius: 1.5, px: 1, py: 0.5, mx: -1 }
                  : undefined
              }
            >
              {isBasic ? (
                <CheckRoundedIcon sx={{ fontSize: 18, color: 'text.secondary', mt: '1px' }} />
              ) : (
                <AddRoundedIcon sx={{ fontSize: 18, color: featured ? 'primary.main' : 'success.main', mt: '1px' }} />
              )}
              {engineIds ? (
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ pt: '1px' }}>
                  {engineIds.map((id) => (
                    <EngineLogo key={id} engine={id} size={18} />
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" fontWeight={highlighted ? 700 : 400}>{text}</Typography>
              )}
            </Stack>
          );
        })}
      </Stack>

      <Button
        fullWidth
        variant={state === 'current' ? 'outlined' : featured ? 'contained' : 'outlined'}
        color={state === 'current' ? 'success' : 'primary'}
        disabled={disabled || state === 'current' || state === 'downgrade'}
        onClick={onChoose}
        startIcon={
          busy ? <CircularProgress size={14} color="inherit" /> : state === 'current' ? <CheckRoundedIcon /> : undefined
        }
        sx={{ mt: 3 }}
      >
        {busy
          ? '…'
          : state === 'current'
            ? t('planSelection.currentPlan')
            : state === 'downgrade'
              ? t('planSelection.includedInPlan')
              : t('planSelection.choose')}
      </Button>
    </Box>
  );
}
